import { useIsPreviewOrDemo } from "../../../contexts/IsPreviewOrDemoContext";
import { useCurrentSite } from "../../../contexts/models/SiteContext";
import {
  Configuration,
  Section,
  StripeRegistryDetails,
} from "../../../resources/modelTypes";
import { themeTypographyResetStyles } from "../../../website/components/themeTypographyResetStyles";
import { selectors } from "../../cypressSelectors";
import { FormFieldWrapper } from "../../shared-components/FormFieldWrapper";
import { StripeRegistryProgressBar } from "../../shared-components/StripeRegistryProgressBar";
import {
  getGuestGiftAmountErrorMessage,
  isGuestGiftAmountValid,
} from "../../stripeAmountValidation";
import { formatDollars, QUICK_OPTION_DOLLARS } from "../../stripeUtils";
import { DisplayPhotoOrVideo } from "../DisplayPhotoOrVideo";
import { useGuestStripe } from "../GuestStripeContext";
import CashRegistryDefaultImage from "../assets/cashRegistryDefaultSmall.webp";
import { css } from "@emotion/react";
import {
  Button,
  Icon,
  LoadingSpinner,
  Modal,
  styleUtils,
  Textarea,
  TextInput,
  theme,
} from "@minted/minted-components";
import {
  CheckoutElementsProvider,
  PaymentElement,
  useCheckoutElements,
} from "@stripe/react-stripe-js/checkout";
import React, { memo, useCallback, useState } from "react";

enum ContributionStep {
  Details,
  GiverInfo,
  Checkout,
  ThankYou,
}

type StripeContributionModalProps = {
  configuration: Configuration;
  onClose: () => void;
  onPaymentComplete: () => void;
  section: Section;
};

const styles = {
  amountChipRow: css`
    display: grid;
    gap: ${theme.spacing.x2};
    grid-template-columns: repeat(4, 1fr);
    margin-top: ${theme.spacing.x4};
  `,
  amountChip: css`
    ${theme.typeStyles.captionMediumStyles};
    align-items: center;
    border: ${styleUtils.rem(1)} solid ${theme.colors.borderDefault};
    border-radius: ${styleUtils.rem(4)};
    color: ${theme.colors.textBrand};
    display: flex;
    flex-direction: column;
    height: ${styleUtils.rem(40)};
    justify-content: center;
    padding-left: ${theme.spacing.base};
    padding-right: ${theme.spacing.base};
    text-transform: none;
    width: ${styleUtils.rem(68)};
    &:hover {
      border: ${styleUtils.rem(1)} solid ${theme.colors.borderBrand};
      cursor: pointer;
    }
  `,
  amountChipEmphasis: css`
    ${theme.typeStyles.textEmphasis};
  `,
  amountChipSelected: css`
    background-color: ${theme.colors.textBrand};
    color: ${theme.colors.white};
  `,
  checkoutError: css`
    ${theme.typeStyles.bodySmallStyles}
    color: ${theme.colors.error};
    margin-top: ${theme.spacing.x4};
    text-align: center;
  `,
  checkoutErrorActions: css`
    display: flex;
    justify-content: center;
    margin-top: ${theme.spacing.x4};
  `,
  coverFeeDisclosure: css`
    ${theme.typeStyles.bodySmallStyles}
    color: ${theme.colors.textSecondary};
    margin-top: ${theme.spacing.base};
  `,
  coverFeeLabel: css`
    ${theme.typeStyles.bodySmallStyles}
    align-items: flex-start;
    cursor: pointer;
    display: flex;
    gap: ${theme.spacing.x2};
    margin-top: ${theme.spacing.x4};
  `,
  description: css`
    ${theme.typeStyles.bodySmallStyles}
    color: ${theme.colors.textSecondary};
    margin-top: ${theme.spacing.x2};
  `,
  disclaimer: css`
    ${theme.typeStyles.bodyExtraSmallStyles}
    color: ${theme.colors.textSecondary};
    margin-top: ${theme.spacing.x3};
    text-align: center;
  `,
  fixedAmountText: css`
    ${theme.typeStyles.bodySmallStyles}
    font-weight: 500;
  `,
  formFieldsStack: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x4};
    margin-top: ${theme.spacing.x4};
  `,
  giftAmountInput: css`
    margin-top: ${theme.spacing.x4};
  `,
  imageWrapper: css`
    aspect-ratio: 1;
    border-radius: ${styleUtils.rem(4)};
    height: ${styleUtils.rem(304)};
    overflow: hidden;
    width: 100%;

    & img {
      height: 100%;
      object-fit: cover;
      width: 100%;
    }
  `,
  layout: css`
    display: grid;
    gap: ${theme.spacing.x6};
    grid-template-columns: 1fr 1fr;
    ${theme.media.lessThan(theme.breakpoints.small)(`
      grid-template-columns: 1fr;
    `)}
  `,
  nextButton: css`
    margin-top: ${theme.spacing.x6};
  `,
  pendingMessage: css`
    ${theme.typeStyles.bodyMediumStyles}
    color: ${theme.colors.textSecondary};
    padding-bottom: ${theme.spacing.x10};
    padding-left: ${theme.spacing.x8};
    padding-right: ${theme.spacing.x8};
    padding-top: ${theme.spacing.x10};
    text-align: center;
  `,
  rightColumn: css`
    display: flex;
    flex-direction: column;
  `,
  summaryRow: css`
    ${theme.typeStyles.bodySmallStyles}
    display: flex;
    justify-content: space-between;
    margin-top: ${theme.spacing.x2};
  `,
  thankYouButtonWrapper: css`
    width: 100%;
  `,
  thankYouSubtext: css`
    ${theme.typeStyles.bodyMediumStyles}
    color: ${theme.colors.textSecondary};
    margin-top: ${theme.spacing.x2};
  `,
  thankYouWrapper: css`
    max-width: ${styleUtils.rem(370)};
    justify-self: center;
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x6};
    padding-bottom: ${theme.spacing.x10};
    padding-left: ${theme.spacing.x8};
    padding-right: ${theme.spacing.x8};
    padding-top: ${theme.spacing.x10};
    text-align: center;
  `,
  title: css`
    ${theme.typeStyles.titleExtraLargeStyles}
  `,
};

type ContributionCheckoutFormProps = {
  coverFee: boolean;
  isCoverFeeChanging: boolean;
  onComplete: () => void;
  onCoverFeeChange: (checked: boolean) => void;
};

/** Inner checkout form rendered inside CheckoutElementsProvider. */
const ContributionCheckoutForm = memo(function ContributionCheckoutForm({
  coverFee,
  isCoverFeeChanging,
  onComplete,
  onCoverFeeChange,
}: ContributionCheckoutFormProps) {
  const { approveContribution } = useGuestStripe();
  const { checkout } = useCheckoutElements() as any;
  const isCheckoutReady = Boolean(checkout);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const total = checkout?.total;
  const surcharge = total?.surcharge as
    | { amount: string; minorUnitsAmount: number }
    | undefined;
  const isSurchargeComplete = checkout?.surcharge
    ? checkout.surcharge.status === "complete"
    : true;

  const onRequiresApproval = useCallback(async () => {
    // Rejecting here aborts the confirmation; approveContribution throws on
    // any non-success response, which is exactly that signal.
    await approveContribution(checkout.id);
  }, [approveContribution, checkout]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setIsSubmitting(true);
      setSubmissionError(null);
      try {
        const result = await checkout.confirm({
          // If we ever add a redirect payment method (like cash app) we'll need
          // to support a return URL that displays an appropriate thank you
          redirect: "if_required",
          returnUrl: window.location.href,
          ...({ onRequiresApproval } as any),
        });
        if (result?.error) {
          setSubmissionError(
            result.error.message ?? "Payment failed. Please try again.",
          );
        } else {
          onComplete();
        }
      } catch (error) {
        console.error(error);
        setSubmissionError("Payment failed. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [checkout, onRequiresApproval, onComplete],
  );

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ fields: { billingDetails: "auto" } }} />
      <label css={styles.coverFeeLabel} htmlFor="cover-fee">
        <input
          checked={coverFee}
          disabled={isCoverFeeChanging}
          id="cover-fee"
          onChange={({ target: { checked } }) => onCoverFeeChange(checked)}
          type="checkbox"
        />
        <div>
          <strong>I&apos;d like to cover any processing fees</strong>
          <div css={styles.coverFeeDisclosure}>
            A small surcharge may be added based on your payment type and
            billing address, so the couple receives the full gift amount.
          </div>
        </div>
      </label>
      {
        // if surcharge exists on checkout at all then the box has been checked
        checkout?.surcharge && (
          <div css={styles.summaryRow}>
            <span>Processing fee (covered by you)</span>
            <span>
              {isSurchargeComplete
                ? surcharge!.amount
                : checkout.canConfirm
                  ? "Calculating"
                  : "TBD"}
            </span>
          </div>
        )
      }
      {total?.total?.amount && (
        <div css={styles.summaryRow}>
          <span>Total</span>
          <span>{total.total.amount}</span>
        </div>
      )}
      {submissionError && (
        <div css={styles.checkoutError}>{submissionError}</div>
      )}
      <div css={styles.nextButton}>
        <Button
          disabled={
            isCoverFeeChanging ||
            isSubmitting ||
            !isCheckoutReady ||
            !checkout.canConfirm ||
            !isSurchargeComplete
          }
          expand
          loading={isSubmitting}
          submit
          text="Pay"
          type={Button.types.primary}
        />
      </div>
    </form>
  );
});

const StripeContributionModal = ({
  configuration,
  onClose,
  onPaymentComplete,
  section,
}: StripeContributionModalProps) => {
  const site = useCurrentSite();
  const { isPreview } = useIsPreviewOrDemo();
  const {
    createCheckoutSession: requestCheckoutSession,
    stripeFlags: { giftsAllowed, awaitingHostAction },
    stripePromise,
  } = useGuestStripe();
  const siteDetails = site.siteDetails as
    | { yourName?: string; spouseName?: string }
    | undefined;
  const coupleName = [siteDetails?.yourName, siteDetails?.spouseName]
    .filter(Boolean)
    .join(" & ");

  const details = section.sectionDetails as StripeRegistryDetails;

  const [step, setStep] = useState<ContributionStep>(ContributionStep.Details);

  const [coverFee, setCoverFee] = useState(true);
  const [clientSecret, setClientSecret] = useState<string>("");
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);

  const [selectedAmountCents, setSelectedAmountCents] = useState<number | null>(
    details.isFixedAmount ? details.giftAmountCents : null,
  );
  const [isOtherMode, setIsOtherMode] = useState(false);
  const [showAmountError, setShowAmountError] = useState(false);
  const [giverName, setGiverName] = useState("");
  const [giverEmail, setGiverEmail] = useState("");
  const [note, setNote] = useState("");

  const onOtherAmountChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
      if (!/^\d*$/.test(value)) return;
      setSelectedAmountCents(value ? parseInt(value, 10) * 100 : null);
    },
    [],
  );

  const onGiverNameChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
      setGiverName(value),
    [],
  );

  const onGiverEmailChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
      setGiverEmail(value),
    [],
  );

  const onNoteChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLTextAreaElement>) =>
      setNote(value),
    [],
  );

  const isStep1Valid = selectedAmountCents !== null && selectedAmountCents > 0;
  const isStep2Valid =
    giverName.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(giverEmail.trim());

  const otherAmountErrorMessage =
    showAmountError &&
    !details.isFixedAmount &&
    selectedAmountCents !== null &&
    !isGuestGiftAmountValid(selectedAmountCents) &&
    getGuestGiftAmountErrorMessage();

  const onNextClick = useCallback(() => {
    if (
      !details.isFixedAmount &&
      selectedAmountCents !== null &&
      !isGuestGiftAmountValid(selectedAmountCents)
    ) {
      setShowAmountError(true);
      return;
    }
    setStep(ContributionStep.GiverInfo);
  }, [details.isFixedAmount, selectedAmountCents]);

  const renderImage = useCallback(
    () => (
      <div css={styles.imageWrapper}>
        <DisplayPhotoOrVideo
          configuration={configuration}
          defaultImage={CashRegistryDefaultImage}
          isRegistryPhoto
          section={section}
        />
      </div>
    ),
    [configuration, section],
  );

  const renderStep1 = useCallback(
    () => (
      <div
        css={styles.layout}
        data-cy={selectors.stripeRegistry_contributionModal_step1}
      >
        {renderImage()}
        <div css={styles.rightColumn}>
          <div css={styles.title}>{details.title}</div>
          {details.textDescription && (
            <div css={styles.description}>{details.textDescription}</div>
          )}
          <StripeRegistryProgressBar
            fundedCents={details.contributionTotalCents ?? 0}
            goalAmountCents={details.goalAmountCents}
            hideGoal={details.hideGoal}
          />
          <div css={styles.giftAmountInput}>
            <FormFieldWrapper label="Gift Amount">
              {details.isFixedAmount ? (
                <div css={styles.fixedAmountText}>
                  {formatDollars(details.giftAmountCents)}
                </div>
              ) : details.showQuickOptions ? (
                <>
                  <div css={styles.amountChipRow}>
                    {QUICK_OPTION_DOLLARS.map((dollars) => {
                      const cents = dollars * 100;
                      const isSelected =
                        !isOtherMode && selectedAmountCents === cents;
                      return (
                        <div
                          css={[
                            styles.amountChip,
                            isSelected && styles.amountChipSelected,
                          ]}
                          data-cy={`${selectors.stripeRegistry_contributionModal_quickOptionChip}_${dollars}`}
                          key={dollars}
                          onClick={() => {
                            setSelectedAmountCents(cents);
                            setIsOtherMode(false);
                          }}
                        >
                          ${dollars}
                        </div>
                      );
                    })}
                    <div
                      css={[
                        styles.amountChip,
                        isOtherMode && styles.amountChipSelected,
                      ]}
                      data-cy={
                        selectors.stripeRegistry_contributionModal_quickOptionChipOther
                      }
                      onClick={() => {
                        setIsOtherMode(true);
                        setSelectedAmountCents(null);
                      }}
                    >
                      {isOtherMode && selectedAmountCents !== null ? (
                        <>
                          <div>Other </div>
                          <div css={styles.amountChipEmphasis}>
                            ${selectedAmountCents / 100}
                          </div>
                        </>
                      ) : (
                        <>
                          <div>Other </div>
                          <div css={styles.amountChipEmphasis}>$--</div>
                        </>
                      )}
                    </div>
                  </div>
                  {isOtherMode && (
                    <div
                      css={styles.giftAmountInput}
                      data-cy={
                        selectors.stripeRegistry_contributionModal_amountOtherInput
                      }
                    >
                      <TextInput
                        addons={[{ align: "left", content: "$" }]}
                        error={otherAmountErrorMessage}
                        inputMode="numeric"
                        name="giftAmount"
                        onChange={onOtherAmountChange}
                        pattern="[0-9]*"
                        placeholder="Enter amount"
                        touched
                        value={
                          selectedAmountCents !== null
                            ? String(selectedAmountCents / 100)
                            : ""
                        }
                      />
                    </div>
                  )}
                </>
              ) : (
                <div
                  data-cy={
                    selectors.stripeRegistry_contributionModal_amountOtherInput
                  }
                >
                  <TextInput
                    addons={[{ align: "left", content: "$" }]}
                    error={otherAmountErrorMessage}
                    inputMode="numeric"
                    name="giftAmount"
                    onChange={onOtherAmountChange}
                    pattern="[0-9]*"
                    placeholder="Enter amount"
                    touched
                    value={
                      selectedAmountCents !== null
                        ? String(selectedAmountCents / 100)
                        : ""
                    }
                  />
                </div>
              )}
            </FormFieldWrapper>
          </div>
          <div
            css={styles.nextButton}
            data-cy={selectors.stripeRegistry_contributionModal_nextButton}
          >
            <Button
              disabled={!isStep1Valid}
              expand
              onClick={onNextClick}
              text="Next: Add your details"
              type={Button.types.primary}
            />
          </div>
        </div>
      </div>
    ),
    [
      details,
      isOtherMode,
      isStep1Valid,
      onNextClick,
      onOtherAmountChange,
      otherAmountErrorMessage,
      selectedAmountCents,
      renderImage,
    ],
  );

  const createCheckoutSession = useCallback(
    async (coverFeeValue: boolean) => {
      if (selectedAmountCents === null) {
        return false;
      }
      setFetchError(null);
      setFetching(true);
      try {
        const secret = await requestCheckoutSession(section.id, {
          amountCents: selectedAmountCents,
          giverName,
          giverEmail,
          note,
          coverFee: coverFeeValue,
        });
        setClientSecret(secret);
        return true;
      } catch {
        setFetchError("Unable to initialize payment. Please try again.");
        return false;
      } finally {
        setFetching(false);
      }
    },
    [
      requestCheckoutSession,
      section.id,
      selectedAmountCents,
      giverName,
      giverEmail,
      note,
    ],
  );

  const handleProceedToCheckout = useCallback(async () => {
    if (isPreview) {
      // Guests reach real Stripe checkout; a preview render must not create
      // a live checkout session on the host's behalf.
      return;
    }
    const success = await createCheckoutSession(coverFee);
    if (success) {
      setStep(ContributionStep.Checkout);
    }
  }, [coverFee, createCheckoutSession, isPreview]);

  const handleCoverFeeChange = useCallback(
    async (checked: boolean) => {
      const previousCoverFee = coverFee;
      setCoverFee(checked);
      const success = await createCheckoutSession(checked);
      if (!success) {
        setCoverFee(previousCoverFee);
      }
    },
    [coverFee, createCheckoutSession],
  );

  const renderStep2 = useCallback(
    () => (
      <div
        css={styles.layout}
        data-cy={selectors.stripeRegistry_contributionModal_step2}
      >
        {renderImage()}
        <div css={styles.rightColumn}>
          <div css={styles.title}>{details.title}</div>
          <div css={styles.description}>
            {`Share your details so ${coupleName} can track your gift`}
          </div>
          <div css={styles.formFieldsStack}>
            <FormFieldWrapper label="Name">
              <TextInput
                data-cy={selectors.stripeRegistry_contributionModal_nameInput}
                name="giverName"
                onChange={onGiverNameChange}
                placeholder="First and last name"
                touched
                value={giverName}
              />
            </FormFieldWrapper>
            <FormFieldWrapper label="Email">
              <TextInput
                data-cy={selectors.stripeRegistry_contributionModal_emailInput}
                inputMode="email"
                name="giverEmail"
                onChange={onGiverEmailChange}
                placeholder="Email"
                touched
                value={giverEmail}
              />
            </FormFieldWrapper>
            <FormFieldWrapper label="Add a note for the couple (optional)">
              <Textarea
                data-cy={selectors.stripeRegistry_contributionModal_noteInput}
                minRows={3}
                name="note"
                onChange={onNoteChange}
                placeholder="Write a personal note"
                value={note}
              />
            </FormFieldWrapper>
            {fetchError && <div css={styles.checkoutError}>{fetchError}</div>}
            <div
              css={styles.nextButton}
              data-cy={selectors.stripeRegistry_contributionModal_nextButton}
            >
              <Button
                disabled={!isStep2Valid || fetching || isPreview}
                expand
                iconType={Icon.types["open-in-new"]}
                loading={fetching}
                onClick={handleProceedToCheckout}
                text={`Next: Give ${formatDollars(selectedAmountCents ?? 0)}`}
                type={Button.types.primary}
              />
            </div>
          </div>
          <div css={styles.disclaimer}>
            Minted doesn&apos;t process payments. You&apos;ll complete your gift
            securely on Stripe.
          </div>
        </div>
      </div>
    ),
    [
      coupleName,
      details,
      fetchError,
      fetching,
      giverEmail,
      giverName,
      handleProceedToCheckout,
      isPreview,
      isStep2Valid,
      note,
      onGiverEmailChange,
      onGiverNameChange,
      onNoteChange,
      renderImage,
      selectedAmountCents,
    ],
  );

  const handleCheckoutComplete = useCallback(() => {
    setStep(ContributionStep.ThankYou);
    onPaymentComplete();
  }, [onPaymentComplete]);

  const renderStep3 = useCallback(
    () => (
      <div data-cy={selectors.stripeRegistry_contributionModal_step3}>
        {fetchError && <div css={styles.checkoutError}>{fetchError}</div>}
        {fetching && !clientSecret ? (
          <div css={styles.nextButton}>
            <LoadingSpinner />
          </div>
        ) : (
          <CheckoutElementsProvider
            key={clientSecret}
            options={{ clientSecret }}
            stripe={stripePromise}
          >
            <ContributionCheckoutForm
              coverFee={coverFee}
              isCoverFeeChanging={fetching}
              onComplete={handleCheckoutComplete}
              onCoverFeeChange={handleCoverFeeChange}
            />
          </CheckoutElementsProvider>
        )}
      </div>
    ),
    [
      clientSecret,
      coverFee,
      fetchError,
      fetching,
      handleCheckoutComplete,
      handleCoverFeeChange,
      stripePromise,
    ],
  );

  const renderStep4 = useCallback(
    () => (
      <div
        css={styles.thankYouWrapper}
        data-cy={selectors.stripeRegistry_contributionModal_step4}
      >
        <Icon size={Icon.sizes.large} type={Icon.types.gift} />
        <div>
          <div css={styles.title}>Thank you!</div>
          <div css={styles.thankYouSubtext}>
            {`We've let ${coupleName} know you sent this gift with Stripe`}
          </div>
        </div>
        <div
          css={styles.thankYouButtonWrapper}
          data-cy={selectors.stripeRegistry_contributionModal_doneButton}
        >
          <Button
            expand
            onClick={onClose}
            text="Done"
            type={Button.types.primary}
          />
        </div>
      </div>
    ),
    [coupleName, onClose],
  );

  const renderContent = useCallback(() => {
    if (!giftsAllowed) {
      return (
        <div
          css={styles.pendingMessage}
          data-cy={selectors.stripeRegistry_contributionModal_pending}
        >
          {awaitingHostAction
            ? "Stripe onboarding has not yet been completed."
            : "Stripe onboarding is being validated, please check back soon."}
        </div>
      );
    }

    switch (step) {
      case ContributionStep.Details:
        return renderStep1();
      case ContributionStep.GiverInfo:
        return renderStep2();
      case ContributionStep.Checkout:
        return renderStep3();
      case ContributionStep.ThankYou:
        return renderStep4();
    }
  }, [
    giftsAllowed,
    awaitingHostAction,
    step,
    renderStep1,
    renderStep2,
    renderStep3,
    renderStep4,
  ]);

  return (
    <Modal
      closeOnEsc
      displayCloseButton
      mobileFullscreen
      onCloseClick={onClose}
      open
      size={Modal.sizes.medium}
    >
      <Modal.Body>
        <div css={themeTypographyResetStyles}>{renderContent()}</div>
      </Modal.Body>
    </Modal>
  );
};

const MemoizedStripeContributionModal = memo(StripeContributionModal);

export { MemoizedStripeContributionModal as StripeContributionModal };
